import React, { useCallback, useEffect, useState } from "react";
import { z } from "zod";
import { api, extractApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Save, X, Loader2, Image as ImageIcon, Upload } from "lucide-react";
import { useT, useLocalized } from "@/lib/LanguageContext";
import LanguageAwareField from "./LanguageAwareField";
import MediaPickerModal from "./MediaPickerModal";
// Relative on purpose: no "@" alias on the Jest side (cf. LanguageAwareField.jsx).
import useFieldErrors from "../hooks/useFieldErrors";
import { requiredField, withAnyLangRule } from "../lib/validation";
import FieldError, { invalidInputClass } from "../components/ui/FieldError";
import { cn } from "../lib/utils";

const empty = { name: "", quote_fr: "", quote_en: "", url: "", photo_url: "", active: true, order: 0 };

// BUG-030 — ONE cap, consistent everywhere: this is the same number as
// frontend/src/lib/testimonials.js TESTIMONIAL_QUOTE_MAX_LENGTH (the render-side truncation
// every public surface uses) and backend/server.py TestimonialBase's
// TESTIMONIAL_QUOTE_MAX_LENGTH. A regression test (testimonialsBug030CrossSurface.test.js)
// reads this literal by regex, which is why it stays a plain number instead of an import —
// move all three together.
const QUOTE_MAX_LENGTH = 180;

/**
 * A testimonial is an attributed quote: without the attribution or without the quote there
 * is nothing to publish, and the public renderers would print `« »` under a blank name.
 *
 * The quote rule is `withAnyLangRule`, not `frRequiredField`: TestimonialBase accepts an
 * empty `quote_fr` (the localized reader falls back to the other language), so ONE language
 * is genuinely enough here — unlike the catalogue's `name_fr`, which the API refuses empty.
 * The max mirrors the server's own ceiling; the field's `maxLength` stops typing past it,
 * this stops a pasted or legacy value.
 */
const testimonialSchema = withAnyLangRule(
  z.object({
    name: requiredField(200),
    // `.optional()`: an absent language is what withAnyLangRule is FOR (it treats a
    // non-string as blank), whereas an aborted parse would report zod's own English
    // "Required" on a field the practitioner is looking at.
    quote_fr: z.string().trim().max(QUOTE_MAX_LENGTH, "validation.tooLong").optional(),
    quote_en: z.string().trim().max(QUOTE_MAX_LENGTH, "validation.tooLong").optional(),
  }),
  "quote_fr",
  "quote_en",
);

export default function TestimonialsAdmin() {
  const t = useT("admin");
  const tv = useT("common");
  const loc = useLocalized();
  const { fieldErrors, validate, clearFieldError, clearAllErrors } = useFieldErrors();
  const [items, setItems] = useState([]);
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false);

  // An error is a statement about the value at submit time: the next keystroke retires it.
  const updateField = (k, v) => { setForm((f) => ({ ...f, [k]: v })); clearFieldError(k); };

  // One input, two languages, ONE message slot. The pair rule ("at least one language") is
  // reported on quote_fr by withAnyLangRule, while the per-language ceiling is reported on
  // whichever language broke it — both have to reach the single field the practitioner can
  // see, or a save could be refused with nothing highlighted. A keystroke in either
  // language retires both, because either one can be what fixes the pair.
  const quoteError = fieldErrors.quote_fr || fieldErrors.quote_en;
  const updateQuote = (lang, v) => {
    setForm((f) => ({ ...f, [`quote_${lang}`]: v }));
    clearFieldError("quote_fr");
    clearFieldError("quote_en");
  };

  // BUG-057 — A FAILED LOAD IS NOT AN EMPTY LIST. This had no `catch`: any failure (a
  // 500, the 403 a non-active practice gets, an expired session, a dropped connection)
  // rejected the promise, `setItems` never ran, `items` stayed at its `[]` initial value,
  // and the screen rendered "you have no testimonials yet". A practitioner with twelve
  // published testimonials was told they had none — and the obvious next move is to type
  // them all again.
  //
  // `loadError` is the THIRD outcome the screen has to be able to state, distinct from
  // both "here they are" and "you genuinely have none". Cleared on every attempt, not
  // only set, so a recovered load stops reporting a failure that is over.
  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(Array.isArray(data) ? data : []);
      setLoadError("");
    } catch (e) {
      setLoadError(extractApiError(e, t("testi.loadFailed", null,
        "Your testimonials could not be loaded. This is a loading problem — nothing has been deleted."), tv));
    }
    // `t`/`tv` are the only closed-over values and both are memoised by useT, so this
    // identity is stable and the effect below runs once — naming a non-memoised `load`
    // in that dependency array is an endless refetch, which is why it was omitted.
  }, [t, tv]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (validate(testimonialSchema, form)) { toast.error(tv("validation.fixHighlighted")); return; }
    setBusy(true);
    try {
      const payload = {
        ...form,
        image_url: form.photo_url || form.image_url || "",
        photo_url: form.photo_url || form.image_url || "",
      };
      if (editing === "new") await api.post("/admin/testimonials", payload);
      else await api.put(`/admin/testimonials/${editing.id}`, payload);
      await load();
      setEditing(null);
    } catch (e) {
      toast.error(extractApiError(e, tv("errors.saveFailed"), tv));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (it) => {
    if (!window.confirm(t("testi.confirmDelete", { name: it.name }))) return;
    try {
      await api.delete(`/admin/testimonials/${it.id}`);
      await load();
    } catch (e) {
      toast.error(extractApiError(e, t("toast.deleteFailed"), tv));
    }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/media", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const fullUrl = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, photo_url: fullUrl, image_url: fullUrl }));
    } catch (e) {
      toast.error(extractApiError(e, t("media.toast.uploadFailed"), tv));
    } finally {
      setUploadingPhoto(false);
    }
  };

  return (
    <div data-testid="admin-testimonials">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl text-eh-ink">{t("testi.title")}</h1>
          <p className="text-eh-muted mt-1">{t("testi.subtitle")}</p>
        </div>
        {!editing && (
          <button onClick={() => { setForm(empty); clearAllErrors(); setEditing("new"); }} className="btn-primary text-sm" data-testid="admin-testi-new">
            <Plus size={16} /> {t("testi.new")}
          </button>
        )}
      </div>

      {!editing && (
        <div className="mt-8 space-y-3">
          {loadError ? (
            <div
              data-testid="admin-testi-load-error"
              className="text-sm bg-red-50 border border-red-200 text-red-800 rounded-2xl p-6 text-center"
            >
              <p className="m-0">{loadError}</p>
              <button
                type="button"
                onClick={load}
                data-testid="admin-testi-retry"
                className="mt-3 underline"
              >
                {t("testi.retry", null, "Try again")}
              </button>
            </div>
          ) : items.length === 0 ? (
            <div className="text-eh-muted text-sm bg-eh-cream border border-eh-border-soft rounded-2xl p-8 text-center">
              {t("testi.empty")}
            </div>
          ) : null}
          {items.map((it) => {
            const photo = it.photo_url || it.image_url;
            return (
              <div key={it.id} className="bg-eh-cream border border-eh-border-soft rounded-2xl p-5 flex items-start gap-5">
                {photo ? (
                  <img src={photo} alt={it.name} className="w-14 h-14 rounded-full object-cover shrink-0 border border-eh-border-soft" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-eh-bg border border-eh-border-soft shrink-0 flex items-center justify-center text-eh-muted">
                    <ImageIcon size={20} />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${it.active ? "bg-eh-sage/30 text-eh-sage-deep" : "bg-eh-border-soft text-eh-muted"}`}>
                      {it.active ? t("testi.active") : t("testi.hidden")}
                    </span>
                    <span className="text-xs text-eh-muted">{t("testi.order")} {it.order}</span>
                  </div>
                  <div className="font-script text-2xl text-eh-ochre-deep mt-1">{it.name}</div>
                  <div className="italic text-eh-ink/80 mt-1 text-sm line-clamp-2">« {loc(it, "quote")} »</div>
                </div>
                <button onClick={() => { setForm({ ...empty, ...it, photo_url: it.photo_url || it.image_url || "" }); clearAllErrors(); setEditing(it); }} className="p-2 rounded-full hover:bg-eh-bg" data-testid={`admin-testi-edit-${it.id}`}>
                  <Edit3 size={16} />
                </button>
                <button onClick={() => remove(it)} className="p-2 rounded-full hover:bg-red-50 text-red-600">
                  <Trash2 size={16} />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="mt-6 bg-eh-cream border border-eh-border-soft rounded-3xl p-8" data-testid="admin-testi-editor">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-display text-2xl text-eh-ink">{editing === "new" ? t("testi.newTitle") : t("testi.editTitle")}</h2>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(null)} className="btn-ghost text-sm"><X size={16} /> {t("bookings.cancel")}</button>
              <button onClick={save} disabled={busy} className="btn-primary text-sm" data-testid="admin-testi-save">
                {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} {t("actions.save")}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <TextField
              label={t("testi.field.name")}
              value={form.name}
              onChange={(v) => updateField("name", v)}
              testid="testi-name"
              error={fieldErrors.name ? tv(fieldErrors.name) : ""}
            />
            <TextField label={t("testi.field.link")} value={form.url} onChange={(v) => setForm({ ...form, url: v })} placeholder="https://maps.app.goo.gl/..." />

            {/* Photo Selection (Upload or Media Library) */}
            <div className="md:col-span-2 bg-white border border-eh-border-soft rounded-2xl p-4">
              <label className="text-xs uppercase tracking-[0.18em] text-eh-muted font-medium">Testimonial Photo (Optional)</label>
              <div className="mt-2 flex items-center gap-4 flex-wrap">
                {form.photo_url ? (
                  <img src={form.photo_url} alt="Preview" className="w-16 h-16 rounded-full object-cover border border-eh-border-soft shrink-0" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-eh-bg border border-eh-border-soft shrink-0 flex items-center justify-center text-eh-muted">
                    <ImageIcon size={22} />
                  </div>
                )}
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="btn-ghost text-xs cursor-pointer inline-flex items-center gap-1" data-testid="testi-upload-photo-label">
                    {uploadingPhoto ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => e.target.files?.[0] && uploadPhoto(e.target.files[0])}
                      className="hidden"
                      data-testid="testi-photo-upload"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setMediaPickerOpen(true)}
                    className="btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid="testi-choose-media"
                  >
                    <ImageIcon size={14} /> Choose from Media Library
                  </button>
                  {form.photo_url && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, photo_url: "", image_url: "" })}
                      className="text-xs text-red-600 hover:underline"
                      data-testid="testi-remove-photo"
                    >
                      Remove photo
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Single Language-Aware Quote Field */}
            <div className="md:col-span-2">
              <LanguageAwareField
                label={t("testi.field.quote", null, "Testimonial Quote")}
                valueFr={form.quote_fr}
                valueEn={form.quote_en}
                onChangeFr={(v) => updateQuote("fr", v)}
                onChangeEn={(v) => updateQuote("en", v)}
                requiredLang="any"
                error={quoteError ? tv(quoteError) : ""}
                multiline
                rows={4}
                maxLength={QUOTE_MAX_LENGTH}
                counterTestId="testi-char-count"
                counterText={t("testi.field.charCount", {
                  count: Math.max((form.quote_fr || "").length, (form.quote_en || "").length),
                  max: QUOTE_MAX_LENGTH,
                })}
                testid="testi-quote"
              />
            </div>

            <TextField label={t("testi.order")} type="number" value={form.order} onChange={(v) => setForm({ ...form, order: parseInt(v || "0", 10) })} />
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{t("testi.field.status")}</label>
              <label className="mt-2 flex items-center gap-3 cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="w-4 h-4 accent-eh-sage-deep" />
                <span className="text-sm text-eh-ink">{form.active ? t("testi.active") : t("testi.hidden")}</span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Media Picker Modal */}
      <MediaPickerModal
        isOpen={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onSelect={(url) => setForm((f) => ({ ...f, photo_url: url, image_url: url }))}
      />
    </div>
  );
}

/**
 * `error` is the message ALREADY RESOLVED by the caller's `t` (state holds an i18n key —
 * see lib/validation.js), and `testid` is what makes the field and its message addressable
 * (`${testid}-error`, the house convention).
 */
const TextField = ({ label, value, onChange, multiline, type = "text", placeholder, maxLength, counterTestId, counterText, testid, error }) => (
  <div className={multiline ? "md:col-span-2" : ""}>
    <div className="flex items-center justify-between gap-2">
      <label className="text-xs uppercase tracking-[0.18em] text-eh-muted">{label}</label>
      {maxLength != null && (
        <span className="text-xs text-eh-muted" data-testid={counterTestId}>{counterText}</span>
      )}
    </div>
    {multiline ? (
      <textarea
        rows={4}
        value={value}
        maxLength={maxLength}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={cn(
          "mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-4 py-2.5 text-eh-ink focus:outline-none focus:border-eh-sage-deep resize-none text-sm",
          error && invalidInputClass,
        )}
        data-testid={testid}
      />
    ) : (
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        className={cn(
          "mt-1 w-full bg-white border border-eh-border-soft rounded-xl px-4 py-2.5 text-eh-ink focus:outline-none focus:border-eh-sage-deep text-sm",
          error && invalidInputClass,
        )}
        data-testid={testid}
      />
    )}
    <FieldError message={error} testId={testid ? `${testid}-error` : undefined} />
  </div>
);
